import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'

import Rightbar from 'pages/main/documentsPreparation/Rightbar'
import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'
import PeriodPicker from 'pages/parts/PeriodPicker'
import useFilterSearchPagination from 'pages/parts/useFilterSearchPagination'

import { httpOk, GET, FILE_D } from 'helpers/api'
import { formatMoney, returnSign, formatDate, findKeyFromArrayByValue } from 'helpers/helpers'
import { businessToday, businessMonthStart } from 'helpers/businessDay'

function Index() {
	const { t } = useTranslation()
	const reduxAccount = useSelector(state => state.account)

	const groupBy = [
		{ 'id': 'user', 'name': t('employee') },
		{ 'id': 'menu', 'name': t('menu') },
		{ 'id': 'product', 'name': t('product') },
	];

	const [data, setData] = useState({
		'data': [],
	})
	const [menu, setMenu] = useState([])
	const [poses, setPoses] = useState([])
	const [products, setProducts] = useState([])
	const [users, setUsers] = useState([])
	const [showRightBar, setShowRightBar] = useState(false)
	const [selectedItem, setSelectedItem] = useState({})
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const {
		filterData,
		setFilterData,
		filterDataExist,
		setFilterDataExist,
		pagination,
		setPagination,
		applySearch,
		applyFilters,
		paginate: paginateFilters,
		resetFilters,
	} = useFilterSearchPagination({
		initialFilterData: {
			'start_date': businessToday(),
			'end_date': businessToday(),
			'pos_id': reduxAccount.pos_id,
			'menu_ids': null,
			'user_ids': [],
			'search': '',
		},
		initialPagination: {
			url: '/report-preparation',
			response: null
		},
	})

	function buildFilterParams(filterDataCopy = {}, options = {}) {
		const { includePage = true } = options
		var urlParams = ""

		if (filterDataCopy.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + filterDataCopy.pos_id
		if (filterDataCopy.menu_ids && filterDataCopy.menu_ids.length > 0) {
			const ids = filterDataCopy.menu_ids.map(item => item.id).join(',')
			urlParams += returnSign(urlParams) + 'menu_ids=' + ids
		}
		if (filterDataCopy.product_ids && filterDataCopy.product_ids.length > 0) {
			const ids = filterDataCopy.product_ids.map(item => item.id).join(',')
			urlParams += returnSign(urlParams) + 'product_ids=' + ids
		}
		if (filterDataCopy.user_ids && filterDataCopy.user_ids.length > 0) {
			const ids = filterDataCopy.user_ids.map(item => item.id).join(',')
			urlParams += returnSign(urlParams) + 'user_ids=' + ids
		}
		if (filterDataCopy.group_by)
			urlParams += returnSign(urlParams) + 'group_by=' + filterDataCopy.group_by
		if (filterDataCopy.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterDataCopy.start_date
		if (filterDataCopy.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterDataCopy.end_date
		if (filterDataCopy.search)
			urlParams += returnSign(urlParams) + 'search=' + filterDataCopy.search
		if (includePage && filterDataCopy.page)
			urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page

		return urlParams
	}

	async function searchWithFilter(passedData = {}) {
		var filterUrl = "/report-preparation"
		var filterDataCopy = { ...filterData, ...(passedData.filterData || {}) }

		const urlParams = buildFilterParams(filterDataCopy, { includePage: true })

		filterDataCopy.urlParams = urlParams

		if (`/report-preparation${urlParams}` !== filterUrl)
			setFilterDataExist(true)

		filterUrl += urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, 'url': filterUrl, 'response': response.data })
		}

		setFilterData(filterDataCopy)
		setDropdownIsOpen(false)
	}

	async function toggleRightbar(boolean = false, item) {
		if (!item.document_id) return

		if (boolean) {
			const response = await GET(`/documents-preparation/${item.document_id}`, {}, { loader: true })
			if (httpOk(response)) {
				setSelectedItem(response.data)
				setShowRightBar({ ...showRightBar, 'document': true })
			}
		} else {
			setSelectedItem({})
			setShowRightBar({ ...showRightBar, 'document': false })
		}
	}

	async function downloadExcelFile() {
		let urlParams = buildFilterParams({ ...filterData }, { includePage: false })
		urlParams += returnSign(urlParams) + 'excel=true'
		const response = await FILE_D('/report-preparation' + urlParams)
		if (httpOk(response)) {
			const file = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
			const fileUrl = URL.createObjectURL(file)
			const link = document.createElement('a')
			link.href = fileUrl
			link.setAttribute('download', `${t('preparation')}.xlsx`)
			document.body.appendChild(link)
			link.click()
			URL.revokeObjectURL(fileUrl)
		}
	}

	function resetFilter() {
		resetFilters({
			start_date: businessMonthStart(),
			end_date: businessToday(),
			pos_id: null,
			menu_id: null,
			cashier_login: null,
			waiter_login: null,
			search: '',
		}, (nextFilterData) => searchWithFilter({ filterData: nextFilterData }))
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	async function paginate(page) {
		paginateFilters(page)
		searchWithFilter({ filterData: { ...filterData, page } })
	}

	async function getPoses() {
		const response = await GET('/helper/poses')
		if (httpOk(response)) setPoses(response.data)
	}

	async function getMenu() {
		const response = await GET('/helper/menu')
		if (httpOk(response)) setMenu(response.data)
	}

	async function getUsers() {
		const response = await GET('/helper/users')
		if (httpOk(response)) setUsers(response.data)
	}

	async function getProducts() {
		const response = await GET(`/helper/products`)
		if (httpOk(response)) setProducts(response.data)
	}

	useEffect(() => {
		getMenu()
		getPoses()
		getUsers()
		getProducts()
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header">
				<h4 className="vertical-center h-100">
					{t('preparation')} [{data.total}]
				</h4>
			</div>

			<div className="card">
				<div className="card-body">

					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex flex-wrap gap-2">
							<div>
								<FilterSearch
									value={filterData.search}
									onSearchChange={(search) => applySearch(search, (nextFilterData) => {
										searchWithFilter({ filterData: nextFilterData })
									})}>
								</FilterSearch>
							</div>
							<div>
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('pos')}</label>
												<Select
													options={poses}
													value={poses.find(option => option.id === filterData.pos_id) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'pos_id': option.id })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('menu')}</label>
												<Select
													isMulti
													isClearable
													options={menu}
													value={filterData.menu_ids}
													onChange={(options) => setFilterData({ ...filterData, 'menu_ids': options ? options : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('products')}</label>
												<Select
													isMulti
													isClearable
													options={products}
													value={filterData.product_ids}
													onChange={(options) => setFilterData({ ...filterData, 'product_ids': options ? options : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('employee')}</label>
												<Select
													isMulti
													isClearable
													options={users}
													value={filterData.user_ids}
													onChange={(options) => setFilterData({ ...filterData, 'user_ids': options ? options : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('group')}</label>
												<Select
													isClearable
													options={groupBy}
													value={groupBy.find(option => option.id === filterData.group_by) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'group_by': option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-12">
												<label>{t('period')}</label>
												<PeriodPicker
													startDate={filterData.start_date}
													endDate={filterData.end_date}
													onChange={(period) => setFilterData({ ...filterData, ...period })}
													withTime={false}
													minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
													maxDate={new Date(new Date().getFullYear(), 11, 31)}
												/>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary"
														onClick={() => setDropdownIsOpen(false)}>
														{t('cancel')}
													</button>
													<button className="btn btn-primary"
														onClick={() => applyFilters((nextFilterData) => {
															searchWithFilter({ filterData: nextFilterData })
														})}>
														{t('apply')}
													</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							<div>
								<button className="btn btn-outline-primary btn-wide-2"
									onClick={() => downloadExcelFile()}>
									<i className="uil uil-file-download-alt"></i>
								</button>
							</div>
						</div>
						<div className="vertical-center">
							<h4 className="d-flex justify-content-between gap-2">
								<b>{t('amount')}:</b> {formatMoney(data.total_amount)}
							</h4>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{(filterData.start_date || filterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{filterData.start_date &&
											<span>{formatDate(filterData.start_date)}</span>
										}
										{(filterData.start_date && filterData.end_date) &&
											<span>|</span>
										}
										{filterData.end_date &&
											<span>{formatDate(filterData.end_date)}</span>
										}
									</span>
								}
								{filterData.pos_id &&
									<span className="me-2">
										<b>{t('pos')}: </b>
										{findKeyFromArrayByValue(poses, 'id', filterData.pos_id, 'name')}
									</span>
								}
								{filterData.menu_id &&
									<span className="me-2">
										<b>{t('menu')}: </b>
										{findKeyFromArrayByValue(menu, 'id', filterData.menu_id, 'name')}
									</span>
								}
								{filterData.user_id &&
									<span className="me-2">
										<b>{t('employee')}: </b>
										{findKeyFromArrayByValue(users, 'id', filterData.user_id, 'name')}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase"
									onClick={resetFilter}>
									{t('reset')}
								</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '20%' }}>{t('pos')}</th>
									<th style={{ 'width': '10%' }}>{t('menu')}</th>
									<th style={{ 'width': '10%' }}>{t('product')}</th>
									<th style={{ 'width': '20%' }}>{t('employee')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('date')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('quantity')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('cost_price')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('amount')}</th>
								</tr>
							</thead>
							<tbody>
								{data?.data.map((item, index) => (
									<tr key={index} className={`cursor ${item.showSidebar ? ' table-tr-active' : ''}`}
										onClick={() => toggleRightbar(true, item, index)}>
										<td className="text-center">{item.row_number}</td>
										<td className="td-ellipsis">{item.pos_name}</td>
										<td className="td-ellipsis">{item.menu_name}</td>
										<td className="td-ellipsis">{item.product_name}</td>
										<td className="td-ellipsis">{item.user_name}</td>
										<td className="text-center">{formatDate(item.created_at, 'dd.MM.yy HH:mm')}</td>
										<td className="text-end">{formatMoney(item.quantity, 2)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.cost_price)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.total_price)}</td>
									</tr>
								))}
								<tr>
									<td></td>
									<td><b>{t('total')}</b></td>
									<td></td>
									<td></td>
									<td></td>
									<td></td>
									<td className="text-nowrap text-end"><b>{formatMoney(data.total_quantity, 2)}</b></td>
									<td></td>
									<td className="text-nowrap text-end"><b>{formatMoney(data.total_amount)}</b></td>
								</tr>
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={paginate}>
						</FilterPagination>
					}
				</div>
			</div>

			{showRightBar.document &&
				<Rightbar
					showRightBar={showRightBar.document}
					setShowRightBar={setShowRightBar}
					data={selectedItem}>
				</Rightbar>
			}
		</>
	)
}

export default Index